<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { paymentOptions } from '../composables/salesPropertyFormOptions';

const props = defineProps({
  readOnly: { type: Boolean, default: false },
  transaction: { type: Object, required: true },
});
const emit = defineEmits(['back']);
const { t } = useI18n();

const address = computed(() => ({
  ...(props.transaction.property?.address || {}),
  ...(props.transaction.address || {}),
}));
const location = computed(() =>
  [address.value.bairro, address.value.uf || address.value.estado]
    .filter(Boolean)
    .join(' - ')
);
const street = computed(
  () =>
    address.value.endereco ||
    address.value.logradouro ||
    props.transaction.title ||
    t('SALES.COMMON.ADDRESS_UNAVAILABLE')
);
const manager = computed(
  () => props.transaction.manager_details || props.transaction.manager || {}
);
const managerName = computed(
  () =>
    manager.value.display_name ||
    manager.value.subject?.name ||
    manager.value.name ||
    t('SALES.COMMON.UNASSIGNED')
);
const managerBranch = computed(() => manager.value.branch_name || '');
const responsible = computed(
  () => props.transaction.post_sale?.responsible || {}
);
const responsibleName = computed(
  () =>
    responsible.value.display_name ||
    responsible.value.subject?.name ||
    responsible.value.name ||
    ''
);
const initials = name =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
const managerInitials = computed(() => initials(managerName.value));
const responsibleInitials = computed(() => initials(responsibleName.value));
const paymentLabels = computed(() => {
  const named = String(props.transaction.profile?.forma_pagamento_nome || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);
  if (named.length) return named;

  const labels = new Map(
    paymentOptions(t).map(item => [item.value, item.label])
  );
  const raw = props.transaction.profile?.forma_pagamento;
  const values = Array.isArray(raw)
    ? raw
    : String(raw || '')
        .split(',')
        .filter(Boolean);
  return values.map(value => labels.get(String(value))).filter(Boolean);
});
</script>

<template>
  <header class="border-b border-lais-border-soft bg-white">
    <div class="mx-auto w-full max-w-[1500px] px-4 py-5 sm:px-6 lg:px-10">
      <button
        type="button"
        class="inline-flex items-center gap-2 text-sm font-semibold text-lais-text-body hover:text-lais-primary"
        @click="emit('back')"
      >
        <span
          :class="readOnly ? 'i-lucide-arrow-left' : 'i-lucide-log-out'"
          class="size-4"
          aria-hidden="true"
        />
        {{
          readOnly
            ? t('SALES.REVIEW.RETURN_HEADER.BACK')
            : t('SALES.REVIEW.RETURN_HEADER.SAVE_EXIT')
        }}
      </button>

      <div
        class="mt-5 grid gap-5 rounded-lais border border-lais-border-soft bg-lais-bg-soft p-5 md:grid-cols-[1fr_auto] md:items-center"
      >
        <div class="min-w-0">
          <p
            class="flex items-center gap-2 text-sm font-semibold text-lais-text"
          >
            <span
              class="i-lucide-map-pin size-4 text-lais-primary"
              aria-hidden="true"
            />
            {{ location || t('SALES.COMMON.ADDRESS_UNAVAILABLE') }}
          </p>
          <div class="mt-2 flex flex-wrap items-center gap-2">
            <p class="mr-2 text-lg font-semibold text-lais-text">
              {{ street }}
            </p>
            <span
              v-for="label in paymentLabels"
              :key="label"
              class="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700"
            >
              {{ label }}
            </span>
          </div>
        </div>

        <div class="flex flex-col gap-4 xl:flex-row xl:items-center">
          <div
            v-if="responsibleName"
            class="flex items-center gap-3 xl:min-w-44"
          >
            <span
              class="grid size-14 shrink-0 place-items-center rounded-full bg-emerald-600 text-base font-bold text-white"
            >
              {{ responsibleInitials }}
            </span>
            <div class="min-w-0">
              <p class="text-xs text-lais-text-muted">
                {{ t('SALES.REVIEW.RETURN_HEADER.POST_SALE') }}
              </p>
              <p class="truncate font-semibold text-lais-text">
                {{ responsibleName }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-3 xl:min-w-52">
            <span
              class="grid size-14 shrink-0 place-items-center rounded-full bg-lais-primary text-base font-bold text-white"
            >
              {{ managerInitials }}
            </span>
            <div class="min-w-0">
              <p class="text-xs text-lais-text-muted">
                {{ t('SALES.REVIEW.RETURN_HEADER.MANAGER') }}
              </p>
              <p class="truncate font-semibold text-lais-text">
                {{ managerName }}
              </p>
              <p
                v-if="managerBranch"
                class="truncate text-xs text-lais-text-muted"
              >
                {{ managerBranch }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>
